/**
 * VideoGeneratorNode (Canvas V2)
 *
 * Generate video clips from reference images and motion prompts.
 * Features:
 * - Duration slider (3-15s Kling, fixed 8s Veo 3.1)
 * - Motion prompt dual-mode (read: timestamp pills, camera badges,
 *   dialogue cards; edit: textarea)
 * - Inline video preview with hover play, controls on hover
 * - Toolbar: Generate, Expand, Download, Duplicate, Delete
 * - UnifiedBottomPanel (Config, Variants, Audio Remix)
 * - Video lightbox (portal, z-9999)
 * - History with inline players, pending spinners
 * - Model-specific duration snapping
 * - Audio remix UI (TTS provider, voice map, segment rows, save changes)
 * - Stale-run detection: reset running status on mount
 * - 6 connection handles at specified positions
 */

import { memo, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Handle, Position, NodeProps, useReactFlow, type Node as ReactFlowNode } from 'reactflow'
import { buildProjectAssetUrl, executeNode } from '../../../../api/client'
import { getThumbnailUrl } from '../../../../api/useAssetUrl'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import { useCanvasOptional } from '../../CanvasContext'
import { createDebug } from '../../../../utils/debugLog'
import { SlidersHorizontal, LayoutGrid, AudioWaveform, MessageSquarePlus, BookmarkPlus } from 'lucide-react'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  useNodeActions,
  useBottomPanelAutoOpen,
  UnifiedBottomPanel,
  ReferenceSelector,
  RichTextBox,
  CameraCommandsPanel,
  VideoPreviewPlayer,
  ASPECT_RATIO_MAP,
  ASPECT_WIDTH_MAP,
  type RichTextBoxHandle,
  type RichTextBoxState,
  type ToolbarAction,
  type TabDefinition,
  type VideoHistoryEntry,
  type SelectedReference,
  buildAssetCandidates,
  buildCandidates,
  type MentionCandidate,
} from '../shared'
import { useWaveform, WaveformBars, STATUS_INFO as TTS_STATUS_INFO } from '../shared/AudioRemixShared'
import { useAudioMixer, type MixerClip } from './useAudioMixer'
import { VideoConfigTabContent } from '../shared/VideoConfigTabContent'
import { VideoVariantsTabContent } from '../shared/VideoVariantsTabContent'
import { snapOnResolutionChange } from '../shared/videoConstraints'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

import type { VideoGeneratorNodeData } from '../../../../generated/nodeDataTypes'

/* ── Debug logging ── */
const audioRemixLog = createDebug('dw:audio-remix')

/* ── Types ── */

interface TtsVersion {
  label: string
  path: string
}

interface TtsSegment {
  character: string
  path?: string
  original_path?: string
  start_s: number
  end_s: number
  tts_end_s?: number
  emotion?: string
  status?: string
  text: string
  versions?: TtsVersion[]
}

export type { VideoGeneratorNodeData }

/** Video Generator: Config, Variants, Audio */
const VIDEO_TABS: TabDefinition[] = [
  { id: 'config', label: 'Video Config', icon: SlidersHorizontal },
  { id: 'variants', label: 'Variants', icon: LayoutGrid },
  { id: 'audio', label: 'Audio Remix', icon: AudioWaveform },
]

/* ── Motion Prompt Parser ── */

export interface MotionSegment {
  type: 'action' | 'dialogue' | 'voiceover'
  timestamp: string
  startTime: number
  action: string
  camera: string | null
  character?: string
  emotion?: string
}

export interface ParsedMotionPrompt {
  clipHeader: string | null
  segments: MotionSegment[]
  fallbackText: string | null
}

/** Extract numeric start time from a timestamp like "1.0s-3.5s" */
export function parseStartTime(ts: string): number {
  const match = ts.match(/^(\d+(?:\.\d+)?)s?/)
  return match ? parseFloat(match[1]) : 0
}

/** Parse timestamped segments from a text block */
export function parseTimestampedSegments(
  block: string,
  segType: 'action' | 'dialogue' | 'voiceover',
): MotionSegment[] {
  const timestampRegex = /\[(\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s)\]:?\s*/g
  const matches = [...block.matchAll(timestampRegex)]
  if (matches.length === 0) return []

  return matches.map((match, i) => {
    const start = match.index! + match[0].length
    const end = i < matches.length - 1 ? matches[i + 1].index! : block.length
    let segmentText = block.slice(start, end).trim()

    let camera: string | null = null
    let character: string | undefined
    let emotion: string | undefined

    if (segType === 'action') {
      const cameraMatch = segmentText.match(/Camera:\s*(.+?)$/i)
      if (cameraMatch) {
        camera = cameraMatch[1].trim()
        segmentText = segmentText.slice(0, cameraMatch.index).trim()
      }
    } else if (segType === 'dialogue' || segType === 'voiceover') {
      // New format: [@Character,emotion]:"text"
      const speakerMatch = segmentText.match(/^\[@([^\]]*)\]:?/)
      if (speakerMatch) {
        const speakerParts = speakerMatch[1] // e.g. "Kai,cold" or "narrator"
        const commaIdx = speakerParts.indexOf(',')
        if (commaIdx >= 0) {
          character = speakerParts.slice(0, commaIdx).trim()
          emotion = speakerParts.slice(commaIdx + 1).trim()
        } else {
          character = speakerParts.trim()
        }
        segmentText = segmentText.slice(speakerMatch[0].length).trim()
        segmentText = segmentText.replace(/^[""\u201c]+|[""\u201d]+$/g, '').trim()
      } else {
        // Backward compat: old format "Character: \"text\""
        const colonIdx = segmentText.indexOf(':')
        if (colonIdx > 0) {
          character = segmentText.slice(0, colonIdx).trim()
          segmentText = segmentText.slice(colonIdx + 1).trim()
          segmentText = segmentText.replace(/^[""\u201c]+|[""\u201d]+$/g, '').trim()
        }
      }
    }

    return {
      type: segType,
      timestamp: match[1],
      startTime: parseStartTime(match[1]),
      action: segmentText,
      camera,
      character,
      emotion,
    }
  })
}

export function parseMotionPrompt(text: string): ParsedMotionPrompt {
  if (!text.trim()) return { clipHeader: null, segments: [], fallbackText: null }

  // Extract tagged blocks (<dialogue>, <voiceover>) and the leading action block.
  // Tags can appear in any order; everything before the first tag is the action block.
  const tagRegex = /<(dialogue|voiceover)>\n?/g
  const tagMatches = [...text.matchAll(tagRegex)]

  const actionBlock = tagMatches.length > 0 ? text.slice(0, tagMatches[0].index) : text

  const taggedBlocks: Array<{ type: 'dialogue' | 'voiceover'; content: string }> = []
  for (let i = 0; i < tagMatches.length; i++) {
    const start = tagMatches[i].index! + tagMatches[i][0].length
    const end = i < tagMatches.length - 1 ? tagMatches[i + 1].index! : text.length
    taggedBlocks.push({
      type: tagMatches[i][1] as 'dialogue' | 'voiceover',
      content: text.slice(start, end),
    })
  }

  const actionSegments = parseTimestampedSegments(actionBlock, 'action')
  const allTaggedSegments = taggedBlocks.flatMap(b => parseTimestampedSegments(b.content, b.type))

  if (actionSegments.length === 0 && allTaggedSegments.length === 0) {
    return { clipHeader: null, segments: [], fallbackText: text }
  }

  const firstTsMatch = actionBlock.match(/\[\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s\]/)
  const clipHeader = firstTsMatch
    ? actionBlock.slice(0, firstTsMatch.index).replace(/:\s*$/, '').trim() || null
    : null

  const segments = [...actionSegments, ...allTaggedSegments].sort(
    (a, b) => a.startTime - b.startTime,
  )

  return { clipHeader, segments, fallbackText: null }
}

/* ── Audio Remix Modal — multi-track editor with real-time preview ── */

/** A positioned audio block on the timeline */
interface AudioBlock {
  id: string
  label: string
  audioUrl: string | null
  startS: number
  endS: number
  highlighted: boolean
  fadeInS: number
  fadeOutS: number
  sourceSegmentIdx?: number
  sourceVersionLabel?: string
  sourceDurationS?: number
}

/** A dialogue segment with multiple audio tracks (STT, remix, remix v2...) */
interface AudioSegmentGroup {
  id: string
  character: string
  emotion?: string
  text: string
  status?: string
  type?: 'dialogue' | 'voiceover'
  tracks: AudioBlock[]
}

/** Serializable config for in_audio_remix_config — matches schema tsType */
interface AudioRemixConfig {
  groups: Array<{
    id: string
    character: string
    emotion?: string
    text?: string
    status?: string
    type?: 'dialogue' | 'voiceover'
    tracks: Array<{
      id: string
      label: string
      startS: number
      endS: number
      highlighted: boolean
      fadeInS?: number
      fadeOutS?: number
      sourceSegmentIdx?: number
      sourceVersionLabel?: string
      sourceDurationS?: number
    }>
  }>
}

let _blockId = 0
function nextBlockId() { return `blk_${++_blockId}` }

const AudioEditorModal = memo(function AudioEditorModal({
  projectId,
  origVideoUrl,
  videoDuration,
  segments,
  dialogueEntries,
  config: externalConfig,
  isSavingRemix,
  onConfigChange,
  onReset,
  onSave,
  onClose,
}: {
  projectId: string
  origVideoUrl: string | null
  videoDuration: number
  segments: TtsSegment[]
  dialogueEntries: Array<{ type?: string }> | null
  config: AudioRemixConfig | null
  isSavingRemix: boolean
  onConfigChange: (config: AudioRemixConfig) => void
  onReset: () => void
  onSave: (config: AudioRemixConfig) => void
  onClose: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map())
  const [playheadTime, setPlayheadTime] = useState(0)
  const [videoLoaded, setVideoLoaded] = useState(false)

  // Web Audio API mixer — routes video audio through GainNode,
  // overlays TTS clips via AudioBufferSourceNode per-track.
  const mixer = useAudioMixer(videoRef)

  // ── Playhead RAF ──
  const rafRef = useRef<number>(0)
  useEffect(() => {
    const tick = () => {
      const video = videoRef.current
      if (video) setPlayheadTime(video.currentTime)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  // ── Track state ──
  // Build audio URL for a segment version
  const getVersionUrl = useCallback((segIdx: number, versionLabel: string) => {
    const seg = segments[segIdx]
    if (!seg) return null
    const versions = seg.versions && seg.versions.length > 0
      ? seg.versions
      : [
          ...(seg.original_path ? [{ label: 'orig', path: seg.original_path }] : []),
          ...(seg.path ? [{ label: 'v1', path: seg.path }] : []),
        ]
    const v = versions.find((ver) => ver.label === versionLabel)
    return v?.path ? buildProjectAssetUrl(projectId, v.path) : null
  }, [segments, projectId])

  // Build groups from saved config or TTS segments (default)
  const buildGroupsFromConfig = useCallback((cfg: AudioRemixConfig | null): AudioSegmentGroup[] => {
    if (cfg?.groups?.length) {
      const voiceoverCount = cfg.groups.filter(g => g.tracks.some(t => t.highlighted)).length
      audioRemixLog('buildGroupsFromConfig: from saved config, groups=%d voiceover=%d', cfg.groups.length, voiceoverCount)
      return cfg.groups.map((g) => {
        // Migrate stale highlights: if multiple non-orig tracks are highlighted
        // per group (old default bug), keep only the first one highlighted.
        const nonOrigHighlighted = g.tracks.filter(
          (tr) => (tr.sourceVersionLabel ?? tr.label) !== 'orig' && tr.highlighted,
        )
        const needsMigration = nonOrigHighlighted.length > 1
        if (needsMigration) {
          audioRemixLog(
            'buildGroupsFromConfig: migrating stale highlights for group=%s, had=%d highlighted non-orig',
            g.character,
            nonOrigHighlighted.length,
          )
        }
        return {
          ...g,
          text: g.text ?? '',
          tracks: g.tracks.map((t) => {
            // STT tracks (sourceVersionLabel === "orig") should use the original
            // video URL, not getVersionUrl() which returns a TTS segment's
            // original_path — the wrong audio file.
            const url = t.sourceVersionLabel === 'orig'
              ? origVideoUrl
              : t.sourceSegmentIdx != null && t.sourceVersionLabel
                ? getVersionUrl(t.sourceSegmentIdx, t.sourceVersionLabel)
                : origVideoUrl
            // Fix 2: Migrate stale highlights — only first non-orig stays highlighted
            const shouldBeHighlighted = needsMigration
              ? t === nonOrigHighlighted[0]
              : t.highlighted
            // Fix 4: Only recompute sourceDurationS when the config is known-stale
            // (needsMigration=true). This avoids discarding intentional user resizes
            // while still fixing phantom speed badges from old default bugs.
            return {
              ...t,
              audioUrl: url,
              fadeInS: t.fadeInS ?? 0,
              fadeOutS: t.fadeOutS ?? 0,
              highlighted: shouldBeHighlighted,
              sourceDurationS: needsMigration ? (t.endS - t.startS) : (t.sourceDurationS ?? (t.endS - t.startS)),
            }
          }),
        }
      })
    }
    // Default: build from TTS segments
    audioRemixLog('buildGroupsFromConfig: from TTS segments, count=%d', segments.length)
    return segments.map((seg, i) => {
      const versions = seg.versions && seg.versions.length > 0
        ? seg.versions
        : [
            ...(seg.original_path ? [{ label: 'orig', path: seg.original_path }] : []),
            ...(seg.path ? [{ label: 'v1', path: seg.path }] : []),
          ]
      // Determine group type from dialogue entries
      const entryType = dialogueEntries?.[i]?.type
      const groupType: 'dialogue' | 'voiceover' | undefined = entryType === 'voiceover' ? 'voiceover' : entryType === 'dialogue' ? 'dialogue' : undefined
      // Compute source duration from the timeline slot (not TTS audio duration)
      // so that speed starts at 1.0x when the user hasn't resized the track.
      const sourceDurationS = seg.end_s - seg.start_s
      return {
        id: `seg_${i}`,
        character: seg.character,
        emotion: seg.emotion,
        text: seg.text,
        status: seg.status,
        type: groupType,
        tracks: versions.map((v) => ({
          id: nextBlockId(),
          label: v.label === 'orig' ? 'STT' : v.label,
          audioUrl: v.path ? buildProjectAssetUrl(projectId, v.path) : null,
          startS: seg.start_s,
          endS: seg.end_s,
          highlighted: v.label !== 'orig' && versions.findIndex(vv => vv.label !== 'orig') === versions.indexOf(v),
          fadeInS: 0,
          fadeOutS: 0,
          sourceSegmentIdx: i,
          sourceVersionLabel: v.label,
          sourceDurationS,
        })),
      }
    })
  }, [getVersionUrl, origVideoUrl, segments, projectId, dialogueEntries])

  const [groups, setGroups] = useState<AudioSegmentGroup[]>(() => buildGroupsFromConfig(externalConfig))

  // React to external config changes (e.g. agent modifying via API)
  // Skip changes caused by our own onConfigChange to avoid render loops.
  const selfUpdateRef = useRef(false)
  const prevConfigRef = useRef(externalConfig)
  useEffect(() => {
    if (externalConfig !== prevConfigRef.current) {
      prevConfigRef.current = externalConfig
      if (selfUpdateRef.current) {
        selfUpdateRef.current = false
        return
      }
      setGroups(buildGroupsFromConfig(externalConfig))
    }
  }, [externalConfig, buildGroupsFromConfig])

  // ── Initialize mixer when video is ready + decode all TTS clips ──
  const mixerInitRef = useRef(false)
  useEffect(() => {
    const video = videoRef.current
    if (!video || !origVideoUrl || mixerInitRef.current) return
    const onCanPlay = () => {
      if (mixerInitRef.current) return
      mixerInitRef.current = true
      mixer.init()
      // Decode all TTS clip audio buffers
      for (const group of groups) {
        for (const track of group.tracks) {
          if (track.audioUrl && track.label !== 'STT') {
            mixer.decodeClip(track.id, track.audioUrl)
          }
        }
      }
    }
    if (video.readyState >= 2) onCanPlay()
    else video.addEventListener('canplay', onCanPlay, { once: true })
    return () => video.removeEventListener('canplay', onCanPlay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origVideoUrl])

  // ── Sync mixer clips whenever highlights or positions change ──
  useEffect(() => {
    const clips: MixerClip[] = []
    for (const group of groups) {
      for (const track of group.tracks) {
        if (track.audioUrl && track.label !== 'STT') {
          clips.push({
            id: track.id,
            url: track.audioUrl,
            startS: track.startS,
            endS: track.endS,
            enabled: track.highlighted,
          })
        }
      }
    }
    mixer.updateClips(clips)
  }, [groups, mixer])

  // Sync config to parent on every change
  useEffect(() => {
    const config: AudioRemixConfig = {
      groups: groups.map((g) => ({
        id: g.id,
        character: g.character,
        emotion: g.emotion,
        text: g.text,
        status: g.status,
        type: g.type,
        tracks: g.tracks.map((t) => ({
          id: t.id,
          label: t.label,
          startS: t.startS,
          endS: t.endS,
          highlighted: t.highlighted,
          fadeInS: t.fadeInS || undefined,
          fadeOutS: t.fadeOutS || undefined,
          sourceSegmentIdx: t.sourceSegmentIdx,
          sourceVersionLabel: t.sourceVersionLabel,
          sourceDurationS: t.sourceDurationS,
        })),
      })),
    }
    selfUpdateRef.current = true
    onConfigChange(config)
  }, [groups, onConfigChange])

  // Flat list of all blocks for playback priority
  const allBlocks = useMemo(() => groups.flatMap((g) => g.tracks), [groups])

  // ── Orig track selection ──
  const [origSelStart, setOrigSelStart] = useState<number | null>(null)
  const [origSelEnd, setOrigSelEnd] = useState<number | null>(null)
  const hasOrigSelection = origSelStart !== null && origSelEnd !== null && Math.abs(origSelEnd - origSelStart) > 0.05

  const origBarCount = Math.max(30, Math.round(videoDuration * 33))
  const origWaveformBars = useWaveform(origVideoUrl, origBarCount)

  // ── Determine which tracks are active at playhead (one per group, all play simultaneously) ──
  const activeTrackIds = useMemo(() => {
    const ids = new Set<string>()
    for (const group of groups) {
      for (const track of group.tracks) {
        if (track.highlighted && track.audioUrl && playheadTime >= track.startS && playheadTime < track.endS) {
          ids.add(track.id)
          break
        }
      }
    }
    return ids
  }, [groups, playheadTime])

  // ── Orig waveform: drag to select region ──
  const origTrackRef = useRef<HTMLDivElement>(null)
  const handleOrigMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      const rect = e.currentTarget.getBoundingClientRect()
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      const anchorS = frac * videoDuration
      setOrigSelStart(anchorS)
      setOrigSelEnd(anchorS)
      const handleMouseMove = (ev: MouseEvent) => {
        const f = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width))
        const s = f * videoDuration
        setOrigSelStart(Math.min(anchorS, s))
        setOrigSelEnd(Math.max(anchorS, s))
      }
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [videoDuration],
  )

  // ── Add block from orig selection ──
  const handleAddBlock = useCallback(() => {
    if (!hasOrigSelection || !origVideoUrl) return
    audioRemixLog('add-custom-clip: startS=%.2f endS=%.2f', origSelStart!, origSelEnd!)
    const newTrack: AudioBlock = {
      id: nextBlockId(),
      label: 'clip',
      audioUrl: origVideoUrl,
      startS: origSelStart!,
      endS: origSelEnd!,
      highlighted: true,
      fadeInS: 0,
      fadeOutS: 0,
    }
    // Add as a new single-track group
    setGroups((prev) => [...prev, {
      id: `custom_${newTrack.id}`,
      character: 'Custom clip',
      text: '',
      tracks: [newTrack],
    }])
    setOrigSelStart(null)
    setOrigSelEnd(null)
  }, [hasOrigSelection, origVideoUrl, origSelStart, origSelEnd])

  // ── Block interactions (update tracks within groups) ──
  const updateTrack = useCallback((trackId: string, updater: (t: AudioBlock) => AudioBlock) => {
    setGroups((prev) => prev.map((g) => ({
      ...g,
      tracks: g.tracks.map((t) => t.id === trackId ? updater(t) : t),
    })))
  }, [])

  const toggleHighlight = useCallback((trackId: string) => {
    const group = groups.find((g) => g.tracks.some((t) => t.id === trackId))
    updateTrack(trackId, (t) => {
      audioRemixLog('toggle-highlight: track=%s label=%s character=%s highlighted=%s→%s', trackId, t.label, group?.character, t.highlighted, !t.highlighted)
      return { ...t, highlighted: !t.highlighted }
    })
  }, [updateTrack, groups])

  const removeGroup = useCallback((groupId: string) => {
    audioRemixLog('remove-group: groupId=%s', groupId)
    setGroups((prev) => prev.filter((g) => g.id !== groupId))
  }, [])

  const setTrackFade = useCallback((trackId: string, field: 'fadeInS' | 'fadeOutS', value: number) => {
    updateTrack(trackId, (t) => ({ ...t, [field]: Math.max(0, value) }))
  }, [updateTrack])

  const moveBlock = useCallback((trackId: string, newStartS: number) => {
    updateTrack(trackId, (t) => {
      const dur = t.endS - t.startS
      const s = Math.max(0, Math.min(newStartS, videoDuration - dur))
      return { ...t, startS: s, endS: s + dur }
    })
  }, [videoDuration, updateTrack])

  const resizeBlock = useCallback((trackId: string, edge: 'left' | 'right', newS: number) => {
    updateTrack(trackId, (t) => {
      if (edge === 'left') {
        const s = Math.max(0, Math.min(newS, t.endS - 0.1))
        return { ...t, startS: s }
      } else {
        const e = Math.min(videoDuration, Math.max(newS, t.startS + 0.1))
        return { ...t, endS: e }
      }
    })
  }, [videoDuration, updateTrack])

  // Reorder tracks within a group
  const reorderTrackInGroup = useCallback((groupId: string, fromIdx: number, toIdx: number) => {
    setGroups((prev) => prev.map((g) => {
      if (g.id !== groupId) return g
      const tracks = [...g.tracks]
      const [moved] = tracks.splice(fromIdx, 1)
      tracks.splice(toIdx, 0, moved)
      return { ...g, tracks }
    }))
  }, [])

  // Drag reorder for sub-tracks within a group
  const trackDragState = useRef<{ groupId: string; fromIdx: number } | null>(null)
  const trackDragOverRef = useRef<{ groupId: string; idx: number } | null>(null)
  const [trackDragOverIdx, setTrackDragOverIdx] = useState<{ groupId: string; idx: number } | null>(null)

  const handleTrackDragStart = useCallback((e: React.MouseEvent, groupId: string, trackIdx: number) => {
    e.preventDefault()
    e.stopPropagation()
    trackDragState.current = { groupId, fromIdx: trackIdx }

    const handleMouseMove = (ev: MouseEvent) => {
      const row = (ev.target as HTMLElement).closest('[data-track-row]')
      if (!row) return
      const rowGroupId = row.getAttribute('data-group-id')
      const rowIdx = parseInt(row.getAttribute('data-track-idx') || '-1', 10)
      if (rowGroupId === groupId && rowIdx >= 0) {
        const val = { groupId, idx: rowIdx }
        trackDragOverRef.current = val
        setTrackDragOverIdx(val)
      }
    }

    const handleMouseUp = () => {
      const over = trackDragOverRef.current
      if (trackDragState.current && over) {
        const { fromIdx } = trackDragState.current
        if (over.groupId === groupId && over.idx !== fromIdx) {
          reorderTrackInGroup(groupId, fromIdx, over.idx)
        }
      }
      trackDragState.current = null
      trackDragOverRef.current = null
      setTrackDragOverIdx(null)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [reorderTrackInGroup])

  // ── Block drag handler ──
  const didDragRef = useRef(false)
  const handleBlockDrag = useCallback(
    (e: React.MouseEvent, blockId: string, mode: 'move' | 'resize-left' | 'resize-right') => {
      e.preventDefault()
      e.stopPropagation()
      const block = allBlocks.find((b) => b.id === blockId)
      if (!block) return
      const container = (e.target as HTMLElement).closest('[data-timeline]') as HTMLElement
      if (!container) return
      const rect = container.getBoundingClientRect()
      const startX = e.clientX
      const origStartS = block.startS
      const origEndS = block.endS
      didDragRef.current = false

      const handleMouseMove = (ev: MouseEvent) => {
        if (Math.abs(ev.clientX - startX) > 3) didDragRef.current = true
        const pxPerSec = rect.width / Math.max(videoDuration, 0.1)
        const deltaSec = (ev.clientX - startX) / pxPerSec
        if (mode === 'move') {
          moveBlock(blockId, origStartS + deltaSec)
        } else if (mode === 'resize-left') {
          resizeBlock(blockId, 'left', origStartS + deltaSec)
        } else {
          resizeBlock(blockId, 'right', origEndS + deltaSec)
        }
      }
      const handleMouseUp = () => {
        if (didDragRef.current) {
          const updated = allBlocks.find((b) => b.id === blockId)
          audioRemixLog('block-drag-end: track=%s mode=%s startS=%.2f→%.2f endS=%.2f→%.2f', blockId, mode, origStartS, updated?.startS, origEndS, updated?.endS)
        }
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [allBlocks, videoDuration, moveBlock, resizeBlock],
  )

  // ── Reorder groups via drag ──
  const [dragReorderIdx, setDragReorderIdx] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)
  const handleReorder = useCallback((dragIdx: number, dropIdx: number) => {
    audioRemixLog('reorder-group: from=%d to=%d', dragIdx, dropIdx)
    setGroups((prev) => {
      const next = [...prev]
      const [moved] = next.splice(dragIdx, 1)
      next.splice(dropIdx, 0, moved)
      return next
    })
  }, [])

  const handleRowDragStart = useCallback((e: React.MouseEvent, idx: number) => {
    e.preventDefault()
    e.stopPropagation()
    setDragReorderIdx(idx)
    setDragOverIdx(idx)

    const rows = document.querySelectorAll('[data-block-row]')
    const rowCenters = Array.from(rows).map((r) => {
      const rect = r.getBoundingClientRect()
      return rect.top + rect.height / 2
    })

    const handleMouseMove = (ev: MouseEvent) => {
      // Find which row the cursor is over
      let closest = idx
      let minDist = Infinity
      rowCenters.forEach((center, i) => {
        const dist = Math.abs(ev.clientY - center)
        if (dist < minDist) {
          minDist = dist
          closest = i
        }
      })
      setDragOverIdx(closest)
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      setDragReorderIdx((fromIdx) => {
        setDragOverIdx((toIdx) => {
          if (fromIdx !== null && toIdx !== null && fromIdx !== toIdx) {
            handleReorder(fromIdx, toIdx)
          }
          return null
        })
        return null
      })
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [handleReorder])

  const playheadPct = videoDuration > 0 ? (playheadTime / videoDuration) * 100 : 0

  // ── Per-track solo playback ──
  const [soloTrackId, setSoloTrackId] = useState<string | null>(null)
  const soloTimerRef = useRef<number>(0)
  const handleSoloPlay = useCallback((trackId: string) => {
    const track = allBlocks.find((b) => b.id === trackId)
    const group = groups.find((g) => g.tracks.some((t) => t.id === trackId))
    audioRemixLog('solo-play: track=%s label=%s character=%s range=%.1f-%.1fs url=%s', trackId, track?.label, group?.character, track?.startS, track?.endS, track?.audioUrl)
    const audio = audioRefs.current.get(trackId)
    if (!audio) return
    // Stop current solo
    if (soloTrackId === trackId && !audio.paused) {
      audioRemixLog('solo-stop: track=%s', trackId)
      audio.pause()
      setSoloTrackId(null)
      clearInterval(soloTimerRef.current)
      return
    }
    // Stop any other audio
    audioRefs.current.forEach((a) => { if (!a.paused) a.pause() })
    clearInterval(soloTimerRef.current)
    videoRef.current?.pause()

    // Find the block to get its time bounds
    const block = allBlocks.find((b) => b.id === trackId)
    if (!block) return

    // For TTS clips (v1, v2), the audio file IS the segment — play from 0
    // For orig/clip tracks sourced from the full video, seek to startS
    const isFullVideo = block.audioUrl === origVideoUrl
    audio.currentTime = isFullVideo ? block.startS : 0
    audio.play().catch(() => {})
    setSoloTrackId(trackId)

    // Poll to stop at endS (for full-video sources) or on natural end
    const stopTime = isFullVideo ? block.endS : (block.endS - block.startS)
    soloTimerRef.current = window.setInterval(() => {
      if (audio.currentTime >= stopTime || audio.paused) {
        audio.pause()
        setSoloTrackId(null)
        clearInterval(soloTimerRef.current)
      }
    }, 50)

    const onEnded = () => {
      setSoloTrackId(null)
      clearInterval(soloTimerRef.current)
      audio.removeEventListener('ended', onEnded)
    }
    audio.addEventListener('ended', onEnded)
  }, [soloTrackId, allBlocks, origVideoUrl, groups])

  // ── Per-track fade mode ──
  const [fadeTrackId, setFadeTrackId] = useState<string | null>(null)

  // In fade mode, edge drag adjusts fade instead of resize
  const handleFadeDrag = useCallback(
    (e: React.MouseEvent, blockId: string, edge: 'left' | 'right') => {
      e.preventDefault()
      e.stopPropagation()
      const block = allBlocks.find((b) => b.id === blockId)
      if (!block) return
      const container = (e.target as HTMLElement).closest('[data-timeline]') as HTMLElement
      if (!container) return
      const rect = container.getBoundingClientRect()
      const startX = e.clientX
      const origFadeIn = block.fadeInS
      const origFadeOut = block.fadeOutS
      const blockDur = block.endS - block.startS

      const handleMouseMove = (ev: MouseEvent) => {
        const pxPerSec = rect.width / Math.max(videoDuration, 0.1)
        const deltaSec = (ev.clientX - startX) / pxPerSec
        if (edge === 'left') {
          setTrackFade(blockId, 'fadeInS', Math.min(Math.max(0, origFadeIn + deltaSec), blockDur * 0.5))
        } else {
          setTrackFade(blockId, 'fadeOutS', Math.min(Math.max(0, origFadeOut - deltaSec), blockDur * 0.5))
        }
      }
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [allBlocks, videoDuration, setTrackFade],
  )

  return (
    <div
      data-testid="audio-editor-backdrop"
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        data-testid="audio-editor-modal"
        className="relative w-full max-w-5xl max-h-[90vh] bg-surface-0 rounded-xl shadow-2xl border border-border flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <h2 className="text-sm font-semibold text-foreground">Audio Remix</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onReset}
              className="px-2 py-1 rounded text-[11px] font-medium bg-white/5 text-white/40 border border-white/10 hover:text-white/60 hover:bg-white/10 transition-colors"
              title="Reset to default (rebuild from TTS segments)"
            >
              Reset
            </button>
            <button onClick={onClose} className="text-text-secondary hover:text-foreground transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Video preview */}
          {origVideoUrl && (
            <div className="flex justify-center relative">
              {!videoLoaded && (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black aspect-video">
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
                </div>
              )}
              <video
                ref={videoRef}
                src={origVideoUrl}
                controls
                crossOrigin="anonymous"
                className={`w-full max-h-[35vh] rounded-lg bg-black aspect-video ${videoLoaded ? '' : 'invisible'}`}
                data-testid="audio-editor-video"
                onCanPlay={() => setVideoLoaded(true)}
              />
            </div>
          )}

          {/* Hidden audio elements for solo play */}
          {allBlocks.map((block) =>
            block.audioUrl ? (
              <audio
                key={block.id}
                ref={(el) => { if (el) audioRefs.current.set(block.id, el); else audioRefs.current.delete(block.id) }}
                src={block.audioUrl}
                preload="auto"
                style={{ display: 'none' }}
              />
            ) : null,
          )}

          {/* Timeline ruler */}
          <div className="flex items-end gap-2">
            <div className="w-20 shrink-0" />
            <div className="relative flex-1 h-5 border-b border-white/10">
            {videoDuration > 0 && Array.from({ length: Math.ceil(videoDuration) + 1 }, (_, i) => {
              const pct = (i / videoDuration) * 100
              if (pct > 100) return null
              return (
                <div key={i} className="absolute top-0 bottom-0" style={{ left: `${pct}%` }}>
                  <div className="w-px h-2.5 bg-white/20" />
                  <span className="absolute top-2.5 text-[10px] text-white/30 -translate-x-1/2 tabular-nums select-none">{i}s</span>
                </div>
              )
            })}
            </div>
          </div>

          {/* ── Orig track ── */}
          <div className="flex items-center gap-2">
            <div className="w-20 shrink-0 text-xs text-white/40 font-medium">orig</div>
            <div
              ref={origTrackRef}
              className="relative flex-1 rounded border border-white/[0.06] bg-white/[0.02] cursor-crosshair nodrag nopan"
              style={{ height: 40 }}
              onMouseDown={handleOrigMouseDown}
              data-timeline
            >
              {/* Full waveform bars */}
              <WaveformBars
                bars={origWaveformBars}
                barCount={origBarCount}
                color="#6b7280"
                opacity={0.6}
                className="absolute inset-0 px-0.5 pointer-events-none"
              />

              {/* Selection overlay with ADD button */}
              {hasOrigSelection && (
                <div
                  className="absolute top-0 bottom-0 z-[1] flex items-center justify-center"
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    left: `${(origSelStart! / videoDuration) * 100}%`,
                    width: `${((origSelEnd! - origSelStart!) / videoDuration) * 100}%`,
                    background: 'rgba(250,204,21,0.15)',
                    borderLeft: '2px solid rgba(250,204,21,0.6)',
                    borderRight: '2px solid rgba(250,204,21,0.6)',
                  }}
                >
                  <button
                    onClick={handleAddBlock}
                    className="px-3 py-1 rounded text-xs font-semibold bg-yellow-500/30 hover:bg-yellow-500/50 text-yellow-200 border border-yellow-500/40 transition-colors shadow-sm"
                  >
                    ADD
                  </button>
                </div>
              )}

              {/* Playhead */}
              <div
                className="absolute top-0 bottom-0 w-px bg-red-500 z-[3] pointer-events-none"
                style={{ left: `${playheadPct}%` }}
              />
            </div>
          </div>

          {/* ── Segment groups ── */}
          {groups.map((group, gIdx) => {
            const isVoiceover = group.type === 'voiceover'
            const statusInfo = !isVoiceover && group.status ? TTS_STATUS_INFO[group.status] : null
            return (
              <div
                key={group.id}
                data-block-row
                className={`space-y-1 transition-opacity ${dragReorderIdx !== null && dragReorderIdx === gIdx ? 'opacity-40' : ''}`}
                style={{
                  borderTop: dragOverIdx === gIdx && dragReorderIdx !== null && dragReorderIdx !== gIdx ? '2px solid rgba(148,163,184,0.5)' : undefined,
                }}
              >
                {/* Header: drag handle + status + character + emotion + dialogue */}
                <div className="flex items-center gap-2">
                  <span
                    className="cursor-grab active:cursor-grabbing text-base text-white/15 hover:text-white/40 select-none shrink-0"
                    onMouseDown={(e) => handleRowDragStart(e, gIdx)}
                  >
                    ≡
                  </span>
                  {isVoiceover && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 bg-purple-500/20 text-purple-400" data-testid="voiceover-badge">
                      voiceover
                    </span>
                  )}
                  {statusInfo && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${statusInfo.className}`} title={statusInfo.tooltip}>
                      {group.status}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-white/80">
                    {group.character}
                  </span>
                  {group.emotion && (
                    <span className="text-xs text-white/30 italic">({group.emotion})</span>
                  )}
                  {group.text && (
                    <span className="text-xs text-white/25 truncate flex-1">&ldquo;{group.text}&rdquo;</span>
                  )}
                  {group.id.startsWith('custom_') && (
                    <button
                      onClick={() => removeGroup(group.id)}
                      className="shrink-0 w-4 h-4 flex items-center justify-center rounded text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Remove clip"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Sub-tracks: one row per version (STT, v1, v2...) — reorderable within group */}
                {group.tracks.map((track, tIdx) => {
                  const isActive = activeTrackIds.has(track.id)
                  const leftPct = videoDuration > 0 ? (track.startS / videoDuration) * 100 : 0
                  const widthPct = videoDuration > 0 ? ((track.endS - track.startS) / videoDuration) * 100 : 0
                  const trackDur = track.endS - track.startS
                  const speedRatio = track.sourceDurationS && trackDur > 0 ? track.sourceDurationS / trackDur : 1.0
                  const showSpeedBadge = track.sourceDurationS != null && Math.abs(speedRatio - 1.0) > 0.01
                  return (
                    <div
                      key={track.id}
                      className="flex items-center gap-2 group/track"
                      data-track-row
                      data-group-id={group.id}
                      data-track-idx={tIdx}
                      style={{
                        borderTop: trackDragOverIdx?.groupId === group.id && trackDragOverIdx.idx === tIdx && trackDragState.current?.fromIdx !== tIdx
                          ? '2px solid rgba(148,163,184,0.4)' : '2px solid transparent',
                        opacity: trackDragState.current?.groupId === group.id && trackDragState.current.fromIdx === tIdx ? 0.4 : 1,
                      }}
                    >
                      <div className="w-20 shrink-0 flex items-center gap-1">
                        {group.tracks.length > 1 && (
                          <span
                            className="cursor-grab active:cursor-grabbing text-sm text-white/12 hover:text-white/30 select-none shrink-0"
                            onMouseDown={(e) => handleTrackDragStart(e, group.id, tIdx)}
                          >
                            ≡
                          </span>
                        )}
                        <button
                          onClick={() => handleSoloPlay(track.id)}
                          className={`shrink-0 w-4 h-4 flex items-center justify-center rounded-full transition-colors ${
                            soloTrackId === track.id
                              ? 'bg-red-500/30 text-red-300'
                              : 'bg-white/5 text-white/25 hover:text-white/50 hover:bg-white/10'
                          }`}
                          title={soloTrackId === track.id ? 'Stop' : 'Play'}
                        >
                          {soloTrackId === track.id ? (
                            <svg className="w-2 h-2" viewBox="0 0 8 8" fill="currentColor"><rect width="8" height="8" rx="1" /></svg>
                          ) : (
                            <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="currentColor"><polygon points="2,0 10,5 2,10" /></svg>
                          )}
                        </button>
                        <span className={`text-xs truncate ${track.highlighted ? 'text-white/40' : 'text-white/15'}`}>{track.label}</span>
                      </div>
                      <div
                        className="relative flex-1 rounded border border-white/[0.04] bg-white/[0.01] nodrag nopan"
                        style={{ height: 40 }}
                        data-timeline
                      >
                        <div
                          className={`absolute top-0 bottom-0 rounded cursor-grab active:cursor-grabbing z-[2] group/block ${track.highlighted ? '' : 'opacity-30'}`}
                          style={{
                            left: `${leftPct}%`,
                            width: `${Math.max(widthPct, 0.5)}%`,
                            minWidth: 16,
                            background: track.highlighted ? 'rgba(148,163,184,0.15)' : 'rgba(100,100,100,0.06)',
                            border: `1px solid ${track.highlighted ? (fadeTrackId === track.id ? 'rgba(245,158,11,0.5)' : 'rgba(148,163,184,0.4)') : 'rgba(100,100,100,0.15)'}`,
                            boxShadow: track.highlighted ? '0 0 0 1px rgba(148,163,184,0.15), inset 0 0 0 1px rgba(255,255,255,0.04)' : 'none',
                          }}
                          onClick={() => { if (!didDragRef.current) toggleHighlight(track.id) }}
                          onMouseDown={(e) => handleBlockDrag(e, track.id, 'move')}
                        >
                          <div
                            className={`absolute top-0 bottom-0 left-0 w-[5px] cursor-col-resize z-10 rounded-l ${fadeTrackId === track.id ? 'hover:bg-amber-500/30' : 'hover:bg-white/20'}`}
                            onMouseDown={(e) => { e.stopPropagation(); fadeTrackId === track.id ? handleFadeDrag(e, track.id, 'left') : handleBlockDrag(e, track.id, 'resize-left') }}
                          />
                          <div
                            className={`absolute top-0 bottom-0 right-0 w-[5px] cursor-col-resize z-10 rounded-r ${fadeTrackId === track.id ? 'hover:bg-amber-500/30' : 'hover:bg-white/20'}`}
                            onMouseDown={(e) => { e.stopPropagation(); fadeTrackId === track.id ? handleFadeDrag(e, track.id, 'right') : handleBlockDrag(e, track.id, 'resize-right') }}
                          />
                          <BlockWaveform url={track.audioUrl} accent={track.highlighted ? '#94a3b8' : '#555'} durationS={track.endS - track.startS} />
                          {/* Speed badge — shown when speed ≠ 1.0x */}
                          {showSpeedBadge && (
                            <span
                              className="absolute top-0.5 left-1 z-20 px-1 py-0.5 rounded text-[8px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center gap-0.5"
                              data-testid="speed-badge"
                              title={`Playback speed: ${speedRatio.toFixed(2)}x (source: ${track.sourceDurationS?.toFixed(1)}s, displayed: ${trackDur.toFixed(1)}s)`}
                            >
                              <svg className="w-2 h-2" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zM8 4a.75.75 0 0 0-.75.75v2.69L5.47 9.22a.75.75 0 0 0 1.06 1.06l2-2A.75.75 0 0 0 8.75 7.5V4.75A.75.75 0 0 0 8 4z"/>
                              </svg>
                              {speedRatio.toFixed(1)}x
                            </span>
                          )}
                          {/* Fade toggle button — appears on block hover */}
                          <button
                            className={`absolute top-0.5 right-1 z-20 px-1 py-0.5 rounded text-[8px] font-bold uppercase transition-all ${
                              fadeTrackId === track.id
                                ? 'bg-amber-500/30 text-amber-300 border border-amber-500/40 opacity-100'
                                : 'bg-black/40 text-white/30 border border-white/10 opacity-0 group-hover/block:opacity-100'
                            }`}
                            onClick={(e) => { e.stopPropagation(); audioRemixLog('fade-toggle: track=%s active=%s', track.id, fadeTrackId !== track.id); setFadeTrackId(fadeTrackId === track.id ? null : track.id) }}
                            title={fadeTrackId === track.id ? 'Exit fade mode — edge handles trim' : 'Fade mode — edge handles adjust fade in/out'}
                          >
                            fade
                          </button>
                          {/* Fade in gradient */}
                          {track.fadeInS > 0 && (
                            <div
                              className="absolute top-0 bottom-0 left-0 pointer-events-none"
                              style={{
                                width: `${Math.min((track.fadeInS / (track.endS - track.startS)) * 100, 50)}%`,
                                background: 'linear-gradient(to right, rgba(245,158,11,0.4), transparent)',
                              }}
                            />
                          )}
                          {/* Fade out gradient */}
                          {track.fadeOutS > 0 && (
                            <div
                              className="absolute top-0 bottom-0 right-0 pointer-events-none"
                              style={{
                                width: `${Math.min((track.fadeOutS / (track.endS - track.startS)) * 100, 50)}%`,
                                background: 'linear-gradient(to left, rgba(245,158,11,0.4), transparent)',
                              }}
                            />
                          )}
                        </div>
                        {isActive && (
                          <div
                            className="absolute top-0 bottom-0 w-px bg-red-500 z-[3] pointer-events-none"
                            style={{ left: `${playheadPct}%` }}
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border shrink-0">
          <button
            onClick={() => {
              const config: AudioRemixConfig = {
                groups: groups.map((g) => ({
                  id: g.id,
                  character: g.character,
                  emotion: g.emotion,
                  text: g.text,
                  status: g.status,
                  type: g.type,
                  tracks: g.tracks.map((t) => ({
                    id: t.id,
                    label: t.label,
                    startS: t.startS,
                    endS: t.endS,
                    highlighted: t.highlighted,
                    fadeInS: t.fadeInS || undefined,
                    fadeOutS: t.fadeOutS || undefined,
                    sourceSegmentIdx: t.sourceSegmentIdx,
                    sourceVersionLabel: t.sourceVersionLabel,
                    sourceDurationS: t.sourceDurationS,
                  })),
                })),
              }
              onSave(config)
            }}
            disabled={isSavingRemix}
            className={`w-full py-2 rounded text-sm font-medium transition-all ${
              isSavingRemix
                ? 'bg-surface-2 text-text-muted cursor-not-allowed'
                : 'bg-primary/80 hover:bg-primary text-foreground'
            }`}
          >
            {isSavingRemix ? 'Remixing...' : 'Save & Remix'}
          </button>
        </div>
      </div>
    </div>
  )
})

AudioEditorModal.displayName = 'AudioEditorModal'

/* ── Block waveform: lightweight bars inside a block ── */

const BlockWaveform = memo(function BlockWaveform({ url, accent, durationS }: { url: string | null; accent: string; durationS: number }) {
  const barCount = Math.max(10, Math.round(durationS * 33))
  const bars = useWaveform(url, barCount)
  return (
    <WaveformBars
      bars={bars}
      barCount={barCount}
      color={accent}
      className="absolute inset-0 px-[5px] pointer-events-none overflow-hidden"
    />
  )
})

BlockWaveform.displayName = 'BlockWaveform'

/* ── Render @[[nodeId]] markers as inline pill chips ── */

function renderTextWithChips(
  text: string,
  candidatesMap: Map<string, MentionCandidate>,
  projectId: string,
): ReactNode[] {
  const MARKER_RE = /@\[\[([^\]]+)\]\]/g
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let keyIdx = 0

  while ((match = MARKER_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }

    const nodeId = match[1]
    const candidate = candidatesMap.get(nodeId)

    if (candidate) {
      const thumbUrl = candidate.thumbnailPath
        ? getThumbnailUrl(projectId, candidate.thumbnailPath, 0)
        : null

      parts.push(
        <span
          key={`chip-${keyIdx}`}
          className="inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/15 px-1.5 py-0.5 text-xs text-primary align-baseline mx-0.5"
          data-testid={`mention-chip-${nodeId}`}
        >
          {thumbUrl && (
            <img
              src={thumbUrl}
              alt=""
              className="w-3.5 h-3.5 rounded-sm object-cover flex-shrink-0 pointer-events-none"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          )}
          <span className="inline-flex items-center gap-0.5">
            <span className="text-[11px]">{candidate.icon}</span>
            <span>{candidate.label}</span>
          </span>
        </span>
      )
    } else {
      parts.push(
        <span
          key={`chip-${keyIdx}`}
          className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-xs text-foreground/40 align-baseline mx-0.5 line-through"
          data-testid={`mention-chip-missing-${nodeId}`}
        >
          <span className="inline-flex items-center gap-0.5">
            <span>{nodeId.slice(0, 8)}...</span>
          </span>
        </span>
      )
    }

    keyIdx++
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  if (parts.length === 0) {
    parts.push(text)
  }

  return parts
}

/* ── Formatted Motion Prompt Display ── */

const FormattedMotionPrompt = memo(function FormattedMotionPrompt({
  text,
  onEdit,
  canvasNodes,
  projectId,
}: {
  text: string
  onEdit: () => void
  canvasNodes: ReactFlowNode[]
  projectId: string
}) {
  const parsed = useMemo(() => parseMotionPrompt(text), [text])

  const candidatesMap = useMemo(() => {
    const candidates = buildCandidates(canvasNodes)
    return new Map(candidates.map(c => [c.id, c]))
  }, [canvasNodes])

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs uppercase tracking-wider text-text-muted">Motion Prompt</label>
        <button
          onClick={onEdit}
          className="w-5 h-5 rounded hover:bg-surface-1 text-text-muted hover:text-foreground flex items-center justify-center transition-colors"
          title="Edit"
          data-testid="motion-prompt-edit-btn"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      </div>

      {parsed.fallbackText ? (
        <p className="text-sm text-foreground/80 leading-relaxed">
          {renderTextWithChips(parsed.fallbackText!, candidatesMap, projectId)}
        </p>
      ) : parsed.segments.length === 0 ? (
        <p className="text-sm text-foreground/30 italic">No motion prompt</p>
      ) : (
        <div className="space-y-3">
          {parsed.clipHeader && (
            <p className="text-sm font-bold text-foreground/90">{parsed.clipHeader}</p>
          )}
          {parsed.segments.map((seg, i) => (
            <div key={i} className="space-y-1" data-testid="motion-segment">
              {seg.type === 'voiceover' ? (
                <div className="bg-purple-500/10 rounded-lg p-2 border border-purple-500/20" data-testid="voiceover-card">
                  <span className="text-[9px] uppercase tracking-wider text-purple-400/70 font-medium">voiceover</span>
                  <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium ml-2">
                    {seg.timestamp}
                  </span>
                  {seg.character && (
                    <p className="text-sm font-bold text-foreground/90 mt-1">
                      {seg.character}
                      {seg.emotion && (
                        <span className="text-[9px] italic text-foreground/50 ml-1">{seg.emotion}</span>
                      )}
                    </p>
                  )}
                  <p className="text-sm text-foreground/80 leading-relaxed mt-0.5 italic">
                    &ldquo;{renderTextWithChips(seg.action, candidatesMap, projectId)}&rdquo;
                  </p>
                </div>
              ) : seg.type === 'dialogue' ? (
                <div className="bg-amber-500/10 rounded-lg p-2" data-testid="dialogue-card">
                  <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium">
                    {seg.timestamp}
                  </span>
                  {seg.character && (
                    <p className="text-sm font-bold text-foreground/90 mt-1">
                      {seg.character}
                      {seg.emotion && (
                        <span className="text-[9px] italic text-foreground/50 ml-1">{seg.emotion}</span>
                      )}
                    </p>
                  )}
                  <p className="text-sm text-foreground/80 leading-relaxed mt-0.5">
                    &ldquo;{renderTextWithChips(seg.action, candidatesMap, projectId)}&rdquo;
                  </p>
                </div>
              ) : (
                <>
                  <span className="inline-block text-xs px-1.5 py-0.5 rounded-full bg-primary/20 text-primary/80 font-medium">
                    {seg.timestamp}
                  </span>
                  <p className="text-sm text-foreground/80 leading-relaxed">
                    {renderTextWithChips(seg.action, candidatesMap, projectId)}
                  </p>
                  {seg.camera && (
                    <p className="text-sm">
                      <span className="text-text-muted">Camera</span>{' '}
                      <span className="text-foreground/70">{seg.camera}</span>
                    </p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

FormattedMotionPrompt.displayName = 'FormattedMotionPrompt'

/* ── Main Component ── */

export const VideoGeneratorNode = memo(function VideoGeneratorNode(
  props: NodeProps<VideoGeneratorNodeData>,
) {
  const { id, data, selected, dragging } = props
  const {
    in_name,
    label,
    out_generated_video,
    in_motion_prompt: prompt = '',
    in_duration: duration = 5,
    in_model: model = 'kling-v3-pro',
    in_resolution: resolution = 'auto',
    in_aspect_ratio: aspect_ratio = '16:9',
    in_generate_audio: generate_audio = true,
    in_reference_images,
    status = 'idle',
    progressMessage,
    error,
    out_remixed_video,
    out_tts_segments,
    is_inside_group,
  } = data
  const generated_video = out_generated_video
  const projectId = useProjectId()

  const cssAspectRatio = ASPECT_RATIO_MAP[aspect_ratio] ?? '16/9'
  const nodeWidth = ASPECT_WIDTH_MAP[aspect_ratio] ?? 315

  const { setNodes, getNodes } = useReactFlow()
  const canvas = useCanvasOptional()
  const { handleDelete, handleDuplicate, handleDownload: downloadFile, handleAddToChat, handleSaveToLibrary } = useNodeActions({ nodeId: id })

  // Video preview hover state
  const [isHovering, setIsHovering] = useState(false)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isAudioEditorOpen, setIsAudioEditorOpen] = useState(false)

  // Escape key dismisses video lightbox or audio editor
  useEffect(() => {
    if (!isVideoModalOpen && !isAudioEditorOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        if (isAudioEditorOpen) setIsAudioEditorOpen(false)
        else setIsVideoModalOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isVideoModalOpen, isAudioEditorOpen])

  // Bottom panel state
  const [bottomPanelTab, setBottomPanelTab] = useState('config')
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [isEditingPrompt, setIsEditingPrompt] = useState(false)
  const richTextBoxRef = useRef<RichTextBoxHandle>(null)
  const [localPrompt, setLocalPrompt] = useState(prompt)
  // Sync local prompt when external data changes
  useEffect(() => {
    if (!isEditingPrompt) setLocalPrompt(prompt)
  }, [prompt, isEditingPrompt])

  // Audio remix state
  const [isSavingRemix, setIsSavingRemix] = useState(false)

  // Video duration from the generated video for selection calculations
  const videoDuration = data.out_video_duration || 0

  // History
  const historyEntries = useMemo(
    () => (generated_video?.history ?? []) as VideoHistoryEntry[],
    [generated_video?.history],
  )
  const pickedIndex = generated_video?.picked_index ?? 0

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'
  const batchPending = isRunning ? (data.batch_pending ?? 0) : 0

  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab: setBottomPanelTab,
    defaultTab: 'config',
  })

  // Reference selector state
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)

  const currentRefImages = useMemo(
    () => (in_reference_images ?? []).filter((p): p is string => p != null),
    [in_reference_images],
  )

  const currentRefs = useMemo<SelectedReference[]>(() => {
    if (!currentRefImages.length) return []
    const candidates = buildAssetCandidates(getNodes(), id)
    const pathToCandidate = new Map(candidates.map((c) => [c.assetPath, c]))
    return currentRefImages.map((path, i) => {
      const match = pathToCandidate.get(path)
      return {
        nodeId: match?.nodeId ?? `ref-${i}`,
        nodeType: match?.nodeType ?? 'image',
        label: match?.label ?? path.split('/').pop() ?? `Reference ${i + 1}`,
        assetPath: path,
        mediaType: (match?.mediaType ?? 'image') as 'image' | 'video',
      }
    })
  }, [currentRefImages, getNodes, id])

  const handleRefSave = useCallback(
    (refs: SelectedReference[]) => {
      const paths = refs.map((r) => r.assetPath)
      updateConfig('in_reference_images', paths.length > 0 ? paths : [])
      setIsRefSelectorOpen(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Reset stale running status on mount
  useEffect(() => {
    if (status === 'running') {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  status: 'idle',
                  progress: undefined,
                  progressMessage: undefined,
                  error: 'Generation interrupted. Click Generate to retry.',
                },
              }
            : n,
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-snap on model change
  useEffect(() => {
    if (model === 'seedance-2.0-image-to-video' || model === 'seedance-2.0-reference-to-video') {
      // Seedance: resolution must be 480p or 720p, duration must be in [4,15]
      if (resolution !== '480p' && resolution !== '720p') {
        updateConfig('in_resolution', '720p')
      }
      const clampedDuration = Math.max(4, Math.min(15, duration))
      if (clampedDuration !== duration) {
        updateConfig('in_duration', clampedDuration)
      }
    } else if (model === 'kling-v3-pro') {
      // Kling: resolution must be 'auto', duration must be in [4,12]
      if (resolution !== 'auto') {
        updateConfig('in_resolution', 'auto')
      }
      const clampedDuration = Math.max(4, Math.min(12, duration))
      if (clampedDuration !== duration) {
        updateConfig('in_duration', clampedDuration)
      }
    } else if (model === 'veo-3.1') {
      // Veo: resolution must be in [720p, 1080p, 4k] (not 'auto')
      const newResolution = (resolution === 'auto' || !['720p', '1080p', '4k'].includes(resolution))
        ? '720p'
        : resolution

      if (newResolution !== resolution) {
        updateConfig('in_resolution', newResolution)
      }

      // Snap duration based on target resolution
      const snappedDuration = snapOnResolutionChange(model, newResolution, duration)
      if (snappedDuration !== duration) {
        updateConfig('in_duration', snappedDuration)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])

  // Auto-snap duration on resolution change (within Veo or Seedance)
  useEffect(() => {
    if (model !== 'veo-3.1' && model !== 'seedance-2.0-image-to-video' && model !== 'seedance-2.0-reference-to-video') return

    const snappedDuration = snapOnResolutionChange(model, resolution, duration)
    if (snappedDuration !== duration) {
      updateConfig('in_duration', snappedDuration)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution])

  // Derive current video path from history[picked_index] or fallback
  const videoPath = useMemo(() => {
    if (out_remixed_video) return out_remixed_video
    if (historyEntries.length > 0 && pickedIndex < historyEntries.length) {
      return historyEntries[pickedIndex]?.result?.local_path
    }
    return undefined
  }, [out_remixed_video, historyEntries, pickedIndex])


  const baseVideoUrl =
    videoPath && projectId
      ? buildProjectAssetUrl(projectId, videoPath)
      : null

  const videoUrl = useAuthenticatedMediaUrl(baseVideoUrl, data.cacheKey)

  // Original video URL (before any remix) — always the first generation
  const origVideoPath = useMemo(() => {
    if (historyEntries.length > 0) {
      // Find the first non-remix entry (original generation)
      const orig = historyEntries.find((e) => !e.metadata?.type || e.metadata.type !== 'audio_remix')
      return orig?.result?.local_path ?? historyEntries[0]?.result?.local_path
    }
    return undefined
  }, [historyEntries])
  const baseOrigVideoUrl = origVideoPath && projectId ? buildProjectAssetUrl(projectId, origVideoPath) : null
  const origVideoUrl = useAuthenticatedMediaUrl(baseOrigVideoUrl, data.cacheKey)

  // Build video URL for bottom panel cells
  const buildVideoUrl = useCallback(
    (localPath: string): string | null => {
      if (!projectId) return null
      return buildProjectAssetUrl(projectId, localPath)
    },
    [projectId],
  )

  // Update config
  const updateConfig = useCallback(
    (key: string, value: string | number | boolean | string[] | Record<string, string>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [key]: value } } : n)),
      )
      canvas?.updateNodeData(id, { [key]: value })
    },
    [id, setNodes, canvas],
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      updateConfig('in_name', newName)
    },
    [updateConfig],
  )

  // Generate handler
  const handleGenerate = useCallback(async (retryCount: number = 1) => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') return

    if (!projectId) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: 'Missing projectId.' } }
            : n,
        ),
      )
      return
    }

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
          : n,
      ),
    )

    try {
      await executeNode(projectId, id, undefined, retryCount)
    } catch (err) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
            : n,
        ),
      )
    }
  }, [projectId, id, status, setNodes])

  // Download handler - prefers remixed video
  const downloadPath = useMemo(() => {
    if (out_remixed_video) return out_remixed_video
    if (historyEntries.length > 0 && pickedIndex < historyEntries.length) {
      return historyEntries[pickedIndex]?.result?.local_path
    }
    return undefined
  }, [out_remixed_video, historyEntries, pickedIndex])

  const onDownload = useCallback(() => {
    if (!projectId || !downloadPath) return
    const url = buildProjectAssetUrl(projectId, downloadPath)
    downloadFile(url, downloadPath.split('/').pop() || 'video.mp4')
  }, [projectId, downloadPath, downloadFile])

  // Handle history selection
  const handleHistorySelect = useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const currentNode = getNodes().find((n) => n.id === id)
      const currentGenVideo = currentNode?.data?.out_generated_video
      const newCacheKey = String(Date.now())
      const updatedGenVideo = {
        ...currentGenVideo,
        picked_index: index,
        result: {
          ...currentGenVideo?.result,
          local_path: localPath,
        },
      }

      setNodes((nds) =>
        nds.map((n) =>
          n.id !== id
            ? n
            : {
                ...n,
                data: {
                  ...n.data,
                  out_generated_video: updatedGenVideo,
                  cacheKey: newCacheKey,
                },
              },
        ),
      )
      canvas?.updateNodeData(id, {
        out_generated_video: updatedGenVideo,
        cacheKey: newCacheKey,
      })
    },
    [id, historyEntries, getNodes, setNodes, canvas],
  )



  // Audio remix: save config to YDoc and execute remix
  const handleSaveChangesWithConfig = useCallback(async (config: AudioRemixConfig) => {
    if (!projectId || isSavingRemix) return
    setIsSavingRemix(true)

    const tracksWithSpeed = config.groups.flatMap(g => g.tracks).filter(t => t.sourceDurationS != null && Math.abs((t.sourceDurationS / Math.max(t.endS - t.startS, 0.01)) - 1.0) > 0.01)
    audioRemixLog('save-config: groups=%d tracksWithSpeed=%d', config.groups.length, tracksWithSpeed.length)

    try {
      // Write remix config to YDoc before executing
      canvas?.updateNodeData(id, { in_audio_remix_config: config })
      await executeNode(projectId, id, 'apply_audio_remix')
    } catch (err) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, error: `Remix failed: ${err}` } }
            : n,
        ),
      )
    } finally {
      setIsSavingRemix(false)
    }
  }, [projectId, id, isSavingRemix, setNodes, canvas])

  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'generate',
        label: 'Generate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: () => handleGenerate(1),
        variant: 'primary' as const,
        disabled: showSpinner,
      },
      {
        key: 'expand',
        label: 'Expand',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
            />
          </svg>
        ),
        onClick: () => setIsVideoModalOpen(true),
        hidden: !videoUrl,
        group: true,
      },
      {
        key: 'download',
        label: 'Download',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        ),
        onClick: onDownload,
        hidden: !videoUrl,
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger' as const,
      },
    ],
    [handleGenerate, videoUrl, onDownload, handleDuplicate, handleDelete, handleAddToChat, handleSaveToLibrary, showSpinner],
  )

  // Reference image picker for Config tab
  const referenceImagePicker = useMemo(() => {
    return (
      <div>
        <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">
          References
        </label>
        <div className="flex flex-wrap gap-1">
          {currentRefImages.map((path, i) => (
            <div key={`${path}-${i}`} className="relative group/ref">
              <div className="w-10 h-10 bg-surface-1 rounded border border-border flex items-center justify-center overflow-hidden cursor-pointer" onClick={() => setIsRefSelectorOpen(true)} title={model === 'kling-v3-pro' && i === 0 ? 'Start Frame' : 'Reference'}>
                <img
                  src={getThumbnailUrl(projectId ?? '', path, 80) ?? undefined}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                />
              </div>
              <button
                onClick={() => {
                  const refs = currentRefImages.filter((_, idx) => idx !== i)
                  updateConfig('in_reference_images', refs)
                }}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-xxs flex items-center justify-center opacity-0 group-hover/ref:opacity-100 transition-opacity"
              >
                x
              </button>
            </div>
          ))}
          <button
            onClick={() => setIsRefSelectorOpen(true)}
            className="w-10 h-10 rounded border border-dashed border-border-subtle hover:border-primary/50 text-foreground/30 hover:text-primary/60 flex items-center justify-center transition-colors"
            title="Add reference images"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        </div>
      </div>
    )
  }, [currentRefImages, updateConfig, projectId, model])

  // Build motion prompt + error content for bottom panel
  const motionPromptContent = useMemo(() => {
    return (
      <>
        <div>
          {isEditingPrompt ? (
            <>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs uppercase tracking-wider text-text-muted">
                  Motion Prompt
                </label>
                <button
                  onClick={() => {
                    updateConfig('in_motion_prompt', localPrompt)
                    setIsEditingPrompt(false)
                  }}
                  className="w-5 h-5 rounded hover:bg-surface-1 text-primary/80 hover:text-primary flex items-center justify-center transition-colors"
                  title="Done editing"
                  data-testid="motion-prompt-done-btn"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </button>
              </div>
              <RichTextBox
                ref={richTextBoxRef}
                containerClassName="w-full h-56 bg-surface-1 border border-border rounded px-2 py-1.5 nowheel"
                containerTestId="motion-prompt-textarea"
                canvasNodes={getNodes()}
                placeholder="Describe the video action and motion..."
                initialContent={localPrompt}
                onContentChange={(state: RichTextBoxState) => setLocalPrompt(state.plainText)}
                minHeight={200}
                maxHeight={220}
                maxLength={2000}
                onSubmit={() => {
                  updateConfig('in_motion_prompt', localPrompt)
                  setIsEditingPrompt(false)
                }}
                onEscape={() => {
                  setLocalPrompt(prompt)
                  setIsEditingPrompt(false)
                }}
              />
              <CameraCommandsPanel richTextBoxRef={richTextBoxRef} />
            </>
          ) : (
            <FormattedMotionPrompt
              text={prompt}
              onEdit={() => setIsEditingPrompt(true)}
              canvasNodes={getNodes()}
              projectId={projectId ?? ''}
            />
          )}
        </div>

        {/* Error Banner */}
        {error && (
          <div className="p-2 bg-red-500/10 rounded border border-red-500/30" data-testid="error-banner">
            <span className="text-xxs text-red-400 cursor-help" title={error}>
              {error}
            </span>
          </div>
        )}
      </>
    )
  }, [isEditingPrompt, localPrompt, updateConfig, prompt, error, getNodes, projectId])

  return (
    <div className="relative">
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      {/* Floating label (standalone mode only) */}
      {!is_inside_group && (
        <FloatingNodeLabel
          name={in_name || label || ''}
          fallback="Video Generator"
          onRename={handleRename}
        />
      )}

      <div
        className={`rounded-lg shadow transition-all bg-card border group relative ${
          selected ? 'ring-2 ring-white/70 border-white/40' : 'border-border'
        }`}
        style={{ width: nodeWidth }}
      >
        {/* Connection Handles (6 handles per spec) */}
        <Handle
          type="target"
          position={Position.Top}
          id="in_reference_images"
          className="!bg-transparent !border-0 !w-4 !h-1"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_audio"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '20%' }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_dialogue_prompt"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '50%' }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_voice_map"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '85%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="out_generated_video"
          className="!bg-transparent !border-0 !w-4 !h-1"
          style={{ left: '30%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="out_remixed_video"
          className="!bg-transparent !border-0 !w-4 !h-1"
          style={{ left: '70%' }}
        />

        {/* Preview Area — all 4 corners rounded */}
        <div
          className="relative w-full bg-canvas overflow-hidden cursor-pointer group rounded-lg"
          style={{ aspectRatio: cssAspectRatio }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          data-testid="video-preview-area"
        >
          {showSpinner ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
              <div className={`w-8 h-8 border-2 border-[var(--warning)] border-t-transparent rounded-full ${spinClass}`} />
              <span className="text-xxs text-foreground/50 mt-2 text-center leading-relaxed">
                {isRunning
                  ? (progressMessage || 'Video Generator is rendering your video now')
                  : isPendingCost
                    ? 'Waiting for cost approval in chat...'
                    : 'Queued — waiting for upstream nodes...'}
              </span>
              {isRunning && (
                <span className="text-xxs text-foreground/25 mt-1">This may take up to 2 minutes</span>
              )}
            </div>
          ) : videoUrl ? (
            <>
              <VideoPreviewPlayer
                videoUrl={videoUrl}
                isHovering={isHovering}
                showExpand
                showDownload
                onExpand={() => setIsVideoModalOpen(true)}
                onDownload={onDownload}
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-foreground/20">
              <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span className="text-xxs">No video yet</span>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Panel */}
      <UnifiedBottomPanel
        contentHeight={520}
        tabs={VIDEO_TABS}
        activeTab={bottomPanelTab}
        onTabChange={(tab) => {
          if (tab === 'audio') {
            setIsAudioEditorOpen(true)
          } else {
            setBottomPanelTab(tab)
          }
        }}
        isVisible={!!selected && !dragging && bottomPanelOpen && (!showSpinner || batchPending > 0)}
      >
        {(tab) => {
          if (tab === 'config') {
            return (
              <VideoConfigTabContent
                model={model}
                onModelChange={(m) => updateConfig('in_model', m)}
                referenceImagePicker={referenceImagePicker}
                motionPromptContent={motionPromptContent}
                duration={duration}
                resolution={resolution}
                aspectRatio={aspect_ratio}
                generateAudio={generate_audio}
                onDurationChange={(d) => updateConfig('in_duration', d)}
                onResolutionChange={(r) => updateConfig('in_resolution', r)}
                onAspectRatioChange={(r) => updateConfig('in_aspect_ratio', r)}
                onGenerateAudioChange={(a) => updateConfig('in_generate_audio', a)}
                isRunning={isRunning}
                onGenerate={handleGenerate}
                activeTab={bottomPanelTab}
              />
            )
          }
          if (tab === 'variants') {
            return (
              <VideoVariantsTabContent
                history={historyEntries}
                pickedIndex={pickedIndex}
                onSelectVariant={handleHistorySelect}
                batchPending={batchPending}
                buildVideoUrl={buildVideoUrl}
              />
            )
          }
          return null
        }}
      </UnifiedBottomPanel>

      {/* Reference Selector modal */}
      {isRefSelectorOpen && canvas && projectId && (
        <ReferenceSelector
          isOpen={isRefSelectorOpen}
          onClose={() => setIsRefSelectorOpen(false)}
          onSave={handleRefSave}
          excludeNodeId={id}
          canvasNodes={canvas.getNodes()}
          currentReferences={currentRefs}
          maxReferences={4}
        />
      )}

      {/* Video Preview Lightbox */}
      {isVideoModalOpen &&
        createPortal(
          <div
            data-testid="video-lightbox-backdrop"
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              data-testid="video-lightbox"
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="video-lightbox-close"
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <video
                src={videoUrl ?? undefined}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
              />
            </div>
          </div>,
          document.body,
        )}

      {/* Audio Remix Modal */}
      {isAudioEditorOpen && out_tts_segments && out_tts_segments.length > 0 && projectId &&
        createPortal(
          <AudioEditorModal
            projectId={projectId}
            origVideoUrl={origVideoUrl}
            videoDuration={videoDuration}
            segments={out_tts_segments}
            dialogueEntries={data.in_dialogue_prompt as Array<{ type?: string }> | null ?? null}
            config={data.in_audio_remix_config as AudioRemixConfig | null ?? null}
            isSavingRemix={isSavingRemix}
            onConfigChange={(config) => {
              canvas?.updateNodeData(id, { in_audio_remix_config: config })
            }}
            onReset={() => {
              audioRemixLog('reset-config')
              canvas?.updateNodeData(id, { in_audio_remix_config: null })
            }}
            onSave={(config) => {
              audioRemixLog('save-and-remix: groups=%d', config.groups.length)
              handleSaveChangesWithConfig(config)
              setIsAudioEditorOpen(false)
              setBottomPanelTab('variants')
              setBottomPanelOpen(true)
            }}
            onClose={() => { audioRemixLog('modal-close'); setIsAudioEditorOpen(false) }}
          />,
          document.body,
        )}
    </div>
  )
})

VideoGeneratorNode.displayName = 'VideoGeneratorNode'
